import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, TriangleAlert } from 'lucide-react';
import { EntityImage } from '../world/EntityImage';
import { imageEntryForClass } from '../../utils/entityImages';
import { StatPriorityTable } from './StatPriorityTable';
import { ClassSkills } from './ClassSkills';
import { ClassEquipment } from './ClassEquipment';
import { SourceTag } from './SourceTag';
import type { ClassBuildGuide } from '../../types/classBuild';
import type { CharacterClass } from '../../types/progression';
import type { WorldData } from '../../types/world';
type Props = {
  cls: CharacterClass;
  guide: ClassBuildGuide;
  styleId: string;
  onStyle: (id: string) => void;
  w?: WorldData;
  loading: boolean;
};
const jump = [{
  label: 'Ver habilidades',
  hash: 'habilidades'
}, {
  label: 'Ver equipo',
  hash: 'equipo'
}];
export function ClassBuildPanel({
  cls,
  guide,
  styleId,
  onStyle,
  w,
  loading
}: Props) {
  const style = guide.styles.find(s => s.id === styleId) ?? guide.styles[0];
  const chain = [cls.base, cls.second, cls.third].filter(Boolean) as string[];
  return <article aria-labelledby="clase-t" className="space-y-10" data-id="element-271" data-mp="6zj2ydt2s6by">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6" data-id="element-272" data-mp="292lmim4w0kb">
        <EntityImage entry={imageEntryForClass(cls.id)} alt={cls.base} name={cls.base} lazy={false} kind="persona" size="hero" data-id="element-273" data-mp="26ybvk1k07c4" />
        <div className="min-w-0 flex-1" data-id="element-274" data-mp="3og0t7b5row03">
          <h2 id="clase-t" className="font-cinzel text-2xl font-bold text-white md:text-3xl" data-id="element-275" data-mp="3ohu5b5z5g8fh">
            {cls.base}
          </h2>
          <p className="mt-1 text-sm text-gray-400" data-id="element-276" data-mp="3jjisqnhgzjtk">{chain.join(' → ')}</p>
          <p className="mt-3 max-w-2xl text-gray-200" data-id="element-277" data-mp="3ffumq98hkwaf">{guide.intro}</p>
          <div className="mt-2" data-id="element-278" data-mp="3ahjdo0q0mpt9">
            <SourceTag kind="oficial" href={guide.officialUrl} data-id="element-279" data-mp="3aicdsiugyh2f" />
          </div>
          <nav aria-label={`Accesos de ${cls.base}`} className="mt-4 flex flex-wrap gap-2" data-id="element-280" data-mp="1dmwlbp0n8vdv">
            <Link to={`/guia/evoluciones?clase=${cls.id}`} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl bg-[#D4AF37] px-3.5 text-sm font-semibold text-[#050b14] transition-colors duration-150 hover:bg-[#E8C860]" data-id="element-281" data-mp="18qg135wkf162">
              Ver evolución <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-282" data-mp="1it1uuhn1ek47" />
            </Link>
            {jump.map(j => <a key={j.hash} href={`#${j.hash}`} className="inline-flex min-h-[40px] items-center rounded-xl border border-white/15 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/50" data-id="element-283" data-mp="1nrmvuja457tp">
                {j.label}
              </a>)}
            {guide.related?.map(r => <Link key={r.to} to={r.to} className="inline-flex min-h-[40px] items-center rounded-xl border border-white/15 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/50" data-id="element-284" data-mp="21irp93mw13vl">
                {r.label}
              </Link>)}
          </nav>
        </div>
      </header>

      <section id="estilos" aria-labelledby="estilos-t" className="scroll-mt-24" data-id="element-285" data-mp="v1e9ef8jo19b">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-286" data-mp="ni4yuioaxwio">
          <h3 id="estilos-t" className="font-cinzel text-lg font-bold text-white md:text-2xl" data-id="element-287" data-mp="ik3gjzypa189">
            Estilo de juego
          </h3>
          <SourceTag kind="orientacion" data-id="element-288" data-mp="wijv2d2t0w0y" />
        </div>
        <div role="tablist" aria-label="Estilos de juego" className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-id="element-289" data-mp="rmmjsl6eefv1">
          {guide.styles.map(s => {
          const active = s.id === style.id;
          return <button key={s.id} type="button" role="tab" id={`tab-${s.id}`} aria-selected={active} aria-controls="estilo-panel" onClick={() => onStyle(s.id)} className={`rounded-2xl border p-3 text-left transition-colors duration-150 ${active ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A1628] hover:border-[#D4AF37]/40'}`} data-id="element-290" data-mp="3w1mag13q82gk">
                <span className={`block text-sm font-semibold ${active ? 'text-[#D4AF37]' : 'text-white'}`} data-id="element-291" data-mp="3q02heijjajsu">{s.name}</span>
                <span className="mt-0.5 block text-xs leading-snug text-gray-400" data-id="element-292" data-mp="3uvhuh89pwbo7">{s.summary}</span>
              </button>;
        })}
        </div>
        <div id="estilo-panel" role="tabpanel" aria-labelledby={`tab-${style.id}`} className="mt-4" data-id="element-293" data-mp="8j2reekz0xs1">
          <StatPriorityTable style={style} data-id="element-294" data-mp="cil3qlyq80j9" />
          {style.caution && <p className="mt-3 flex gap-2 text-sm text-amber-200/90" data-id="element-295" data-mp="hi6avjo1vf72">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-296" data-mp="hkit7xxpzrkn" />
              {style.caution}
            </p>}
        </div>
      </section>

      <div className="grid gap-10 md:grid-cols-2" data-id="element-297" data-mp="rgdhp9omkgvc">
        <section aria-labelledby="empezar-t" data-id="element-298" data-mp="2o9oz91m4w3a4">
          <h3 id="empezar-t" className="font-cinzel text-lg font-bold text-white md:text-xl" data-id="element-299" data-mp="2ob03f2jhrd1r">
            Para empezar y levelear
          </h3>
          <ul className="mt-3 space-y-2" data-id="element-300" data-mp="1acvtpp554s3m">
            {guide.startTips.map(t => <li key={t} className="flex gap-2 text-sm text-gray-300" data-id="element-301" data-mp="15mffw3xer4gq">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" aria-hidden="true" data-id="element-302" data-mp="15l47trwxjajt" />
                {t}
              </li>)}
          </ul>
        </section>
        <section aria-labelledby="errores-t" data-id="element-303" data-mp="wnadunmn0szt">
          <h3 id="errores-t" className="font-cinzel text-lg font-bold text-white md:text-xl" data-id="element-304" data-mp="1tbq6isl1nih5">
            Errores frecuentes
          </h3>
          <ul className="mt-3 space-y-2" data-id="element-305" data-mp="1pbpqj3iaqz7z">
            {guide.mistakes.map(t => <li key={t} className="flex gap-2 text-sm text-gray-300" data-id="element-306" data-mp="1ka8v3nil04ys">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400/80" aria-hidden="true" data-id="element-307" data-mp="1fcgzojiqa0pu" />
                {t}
              </li>)}
          </ul>
        </section>
      </div>

      <section id="habilidades" aria-labelledby="habilidades-t" className="scroll-mt-24" data-id="element-308" data-mp="3wipgcbtd8n1">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-309" data-mp="5eh13zgtcea4">
          <h3 id="habilidades-t" className="font-cinzel text-lg font-bold text-white md:text-2xl" data-id="element-310" data-mp="5cedfm34ux8k">
            Habilidades representativas
          </h3>
        </div>
        <p className="mb-3 mt-1 text-sm text-gray-400" data-id="element-311" data-mp="3sif1hllxo7bj">
          Cada habilidad indica cómo se estableció su relación. El requisito que se muestra es el del ítem que enseña el poder, no necesariamente el del poder.
        </p>
        <ClassSkills cls={cls} skills={guide.skills} w={w} loading={loading} data-id="element-312" data-mp="3irao2qbwouit" />
      </section>

      <section id="equipo" aria-labelledby="equipo-t" className="scroll-mt-24" data-id="element-313" data-mp="3nlwxinyfgd09">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-314" data-mp="39ua3kuryjgkn">
          <h3 id="equipo-t" className="font-cinzel text-lg font-bold text-white md:text-2xl" data-id="element-315" data-mp="3dutzxw4pig5q">
            Equipo compatible
          </h3>
          <SourceTag kind="config" data-id="element-316" data-mp="2zwlvl35ccckj" />
        </div>
        <div className="mt-3" data-id="element-317" data-mp="34say9fcnq9l2">
          <ClassEquipment cls={cls} w={w} loading={loading} data-id="element-318" data-mp="34skon41m6ira" />
        </div>
      </section>

      {guide.cautions.length > 0 && <aside className="border-l-2 border-white/15 pl-4 text-sm text-gray-400" data-id="element-319" data-mp="2g8hejj84eb0i">
          {guide.cautions.map(c => <p key={c} data-id="element-320" data-mp="2v60bh8uybuj1">{c}</p>)}
          <a href={guide.officialUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#D4AF37]" data-id="element-321" data-mp="2v71ohivj2r3e">
            Guía oficial de Webzen <ExternalLink className="h-3 w-3" aria-hidden="true" data-id="element-322" data-mp="344u2mjpijsp7" />
          </a>
        </aside>}
    </article>;
}